import { create } from 'zustand';
import { getCustomers } from '../api/generated/customers/customers';
import { useToastStore } from './useToastStore';
import { DEFAULT_PAGE_SIZE, FETCH_ALL_MAX_PAGES, FETCH_ALL_PAGE_SIZE } from '@/constants/pagination';
import type { 
  FacilityFlowCoreModulesCustomersDtosCustomerListDto,
  FacilityFlowCoreModulesCustomersDtosCustomerDto,
  FacilityFlowCoreModulesCustomersWorkflowsCreateCustomerRequest,
  FacilityFlowCoreModulesCustomersWorkflowsUpdateCustomerRequest,
  GetApiV1CustomersParams,
} from '../api/generated/models';

// Type aliases for easier use
type CustomerListDto = FacilityFlowCoreModulesCustomersDtosCustomerListDto;
type CustomerDto = FacilityFlowCoreModulesCustomersDtosCustomerDto;
type CreateCustomerRequest = FacilityFlowCoreModulesCustomersWorkflowsCreateCustomerRequest;
type UpdateCustomerRequest = FacilityFlowCoreModulesCustomersWorkflowsUpdateCustomerRequest;

// Get API functions from factory
const {
  getApiV1Customers,
  getApiV1CustomersId,
  postApiV1Customers,
  putApiV1CustomersId,
  deleteApiV1CustomersId,
} = getCustomers();

interface CustomerState {
  customers: CustomerListDto[];
  selectedCustomer: CustomerDto | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  pageSize: number;
  
  fetchCustomers: (params?: GetApiV1CustomersParams) => Promise<void>;
  fetchAllCustomers: (filter?: { search?: string; status?: string }) => Promise<void>;
  getCustomerById: (id: string) => Promise<CustomerDto | null>;
  createCustomer: (customer: CreateCustomerRequest) => Promise<void>;
  updateCustomer: (id: string, customer: UpdateCustomerRequest) => Promise<void>;
  deleteCustomer: (id: string) => Promise<void>;
}

export const useCustomerStore = create<CustomerState>((set, get) => ({
  customers: [],
  selectedCustomer: null,
  loading: false,
  saving: false,
  error: null,
  totalCount: 0,
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,

  fetchCustomers: async (params?: GetApiV1CustomersParams) => {
    set({ loading: true, error: null });
    try {
      const response = await getApiV1Customers(params);

      set({
        customers: response.items || [],
        totalCount: response.totalCount || 0,
        page: response.page || 1,
        pageSize: response.pageSize || DEFAULT_PAGE_SIZE,
        loading: false,
      });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Laden der Kunden',
        loading: false,
      });
    }
  },

  fetchAllCustomers: async (filter?: { search?: string; status?: string }) => {
    set({ loading: true, error: null });
    try {
      const all: CustomerListDto[] = [];
      let total = 0;
      for (let page = 1; page <= FETCH_ALL_MAX_PAGES; page += 1) {
        const response = await getApiV1Customers({
          ...filter,
          page,
          pageSize: FETCH_ALL_PAGE_SIZE,
        });
        const items = response.items ?? [];
        all.push(...items);
        total = response.totalCount ?? all.length;
        if (items.length === 0 || all.length >= total) break;
      }
      set({ customers: all, totalCount: total, loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Fehler beim Laden der Kunden',
        loading: false,
      });
    }
  },

  getCustomerById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const customer = await getApiV1CustomersId(id);
      set({ selectedCustomer: customer, loading: false });
      return customer;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Fehler beim Laden des Kunden',
        loading: false,
        selectedCustomer: null,
      });
      return null;
    }
  },

  createCustomer: async (customerData: CreateCustomerRequest) => {
    set({ saving: true, error: null });
    try {
      await postApiV1Customers(customerData);

      // Nach Erfolg: vollständige Liste neu laden
      await get().fetchAllCustomers();
      set({ saving: false });
      
      // Success Toast
      useToastStore.getState().addToast('Kunde erfolgreich erstellt', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Erstellen des Kunden';
      set({ 
        error: errorMessage,
        saving: false,
      });
      
      // Error Toast
      useToastStore.getState().addToast(errorMessage, 'error');
      throw error;
    }
  },

  updateCustomer: async (id: string, customerData: UpdateCustomerRequest) => {
    set({ saving: true, error: null });
    try {
      await putApiV1CustomersId(id, customerData);

      // Nach Erfolg: vollständige Liste neu laden
      await get().fetchAllCustomers();
      set({ saving: false });
      
      // Success Toast
      useToastStore.getState().addToast('Kunde erfolgreich aktualisiert', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Aktualisieren des Kunden';
      set({ 
        error: errorMessage,
        saving: false,
      });
      
      // Error Toast
      useToastStore.getState().addToast(errorMessage, 'error');
      throw error;
    }
  },

  deleteCustomer: async (id: string) => {
    set({ saving: true, error: null });
    try {
      await deleteApiV1CustomersId(id);
      
      // Optimistic Update
      set((state) => ({
        customers: state.customers.filter((c) => c.id !== id),
        totalCount: state.totalCount - 1,
        saving: false,
      }));
      
      // Success Toast
      useToastStore.getState().addToast('Kunde erfolgreich gelöscht', 'success');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Löschen des Kunden';
      set({ 
        error: errorMessage,
        saving: false,
      });
      
      // Bei Fehler: vollständige Liste neu laden
      await get().fetchAllCustomers();

      // Error Toast
      useToastStore.getState().addToast(errorMessage, 'error');
      throw error;
    }
  },
}));
